import axios from 'axios';
import API from "../config/API";

/**
 * Get common headers for API requests
 */
function getHeaders() {
    let accessToken = localStorage.getItem('accessToken');
    return {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
}

/**
 * Strip undefined/null values from params before building query strings
 */
function cleanParams(params) {
    return Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
    );
}

/**
 * Transform API response to match frontend expectations
 */
function transformResponse(response) {
    if (response.data && response.data.statusCode === 1 && response.data.responseData) {
        return {
            data: {
                status: response.data.responseData.status,
                message: response.data.responseData.message,
                data: response.data.responseData.data
            }
        };
    }
    return response;
}

/**
 * Finance service for all finance-related API calls
 */
export const financeService = {
    // Overview endpoints
    getRevenueOverview,
    getCommissionOutstanding,
    getCommissionDetail,
    getCommissionPaymentsList,
    collectCommission,
    createCommissionPaymentLink,
    checkCommissionPaymentStatus,
    getTipsCollected,
    getMetrics,
    getPaymentBreakdown,

    // New overview endpoints for production-ready dashboard
    getOutstandingLiabilities,
    getLostRevenue,
    getAppointmentsData,
    getRebookingRate,
    getVouchersData,
    getNewClientsList,
    getTopClients,
    getPerformanceMetrics,
    getOverviewAll,
    getRevenueBySite,

    // Transaction endpoints
    getTransactions,
    getTransactionById,
    getExportCount,
    exportTransactionsCSV,

    // Report endpoints
    getReportTypes,
    generateReport,
    getReport,
    downloadReport
};

//========================== Overview Functions ==========================

/**
 * Get revenue overview data for charts
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} [params.granularity] - Data granularity (daily, weekly)
 * @param {string} [params.compareWith] - Comparison period (previous, year, none)
 * @param {string} [params.hairdresserId] - Filter by staff member
 * @param {string} [params.bookingStatus] - Booking status filter (comma-separated status codes)
 * @returns {Promise} - API response
 */
function getRevenueOverview(params) {
    const headers = getHeaders();

    // Clean up params - remove undefined/null values
    const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v != null && v !== '' && v !== undefined)
    );

    const queryString = new URLSearchParams(cleanParams).toString();

    return axios.get(`${API.FINANCE_OVERVIEW_REVENUE}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Get commission outstanding by staff member
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @returns {Promise} - API response
 */
function getCommissionOutstanding(params) {
    const headers = getHeaders();
    const queryString = new URLSearchParams(cleanParams(params)).toString();

    return axios.get(`${API.FINANCE_OVERVIEW_COMMISSION}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Get commission detail for a specific hairdresser
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.hairdresserId - Hairdresser ID
 * @returns {Promise} - API response with summary and per-booking breakdown
 */
function getCommissionDetail(params) {
    const headers = getHeaders();
    const queryString = new URLSearchParams(cleanParams(params)).toString();

    return axios.get(`${API.FINANCE_OVERVIEW_COMMISSION_DETAIL}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Record a commission collection
 * @param {Object} data - Commission payment data
 * @param {string} data.salonId - Salon ID
 * @param {string} data.hairdresserId - Hairdresser ID
 * @param {number} data.amount - Amount collected
 * @param {string} data.paymentMethod - Payment method (card, cash, bank_transfer)
 * @param {string} [data.notes] - Optional notes
 * @returns {Promise} - API response
 */
function collectCommission(data) {
    const headers = getHeaders();

    return axios.post(API.FINANCE_OVERVIEW_COMMISSION_COLLECT, data, { headers })
        .then(transformResponse);
}

/**
 * Create a commission payment link (Quick Pay via Stripe)
 * @param {Object} data - Payment link data
 * @param {string} data.salonId - Salon ID
 * @param {string} data.hairdresserId - Hairdresser ID
 * @param {number} data.amount - Amount to collect
 * @returns {Promise} - API response with checkoutUrl, sessionId, commissionPaymentId
 */
function createCommissionPaymentLink(data) {
    const headers = getHeaders();

    return axios.post(API.FINANCE_OVERVIEW_COMMISSION_PAYMENT_LINK, data, { headers })
        .then(transformResponse);
}

/**
 * Get paginated list of completed commission payments for a hairdresser
 * @param {Object} params
 * @param {string} params.salonId
 * @param {string} params.hairdresserId
 * @param {number} [params.skip=0]
 * @param {number} [params.limit=20]
 * @returns {Promise}
 */
function getCommissionPaymentsList(params) {
    const headers = getHeaders();
    const queryString = new URLSearchParams(cleanParams(params)).toString();

    return axios.get(`${API.FINANCE_OVERVIEW_COMMISSION_PAYMENTS}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Check commission payment status
 * @param {string} commissionPaymentId - Commission payment ID
 * @returns {Promise} - API response with payment status
 */
function checkCommissionPaymentStatus(commissionPaymentId) {
    const headers = getHeaders();

    return axios.get(`${API.FINANCE_OVERVIEW_COMMISSION_PAYMENT_STATUS}/${commissionPaymentId}`, { headers })
        .then(transformResponse);
}

/**
 * Get tips collected by staff member
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} [params.hairdresserId] - Filter by staff member
 * @returns {Promise} - API response
 */
function getTipsCollected(params) {
    const headers = getHeaders();
    const queryString = new URLSearchParams(cleanParams(params)).toString();

    return axios.get(`${API.FINANCE_OVERVIEW_TIPS}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Get financial metrics (new clients, avg spend)
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} [params.hairdresserId] - Filter by staff member
 * @returns {Promise} - API response
 */
function getMetrics(params) {
    const headers = getHeaders();
    const queryString = new URLSearchParams(cleanParams(params)).toString();

    return axios.get(`${API.FINANCE_OVERVIEW_METRICS}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Get payment method breakdown
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} [params.hairdresserId] - Filter by staff member
 * @returns {Promise} - API response
 */
function getPaymentBreakdown(params) {
    const headers = getHeaders();
    const queryString = new URLSearchParams(cleanParams(params)).toString();

    return axios.get(`${API.FINANCE_OVERVIEW_PAYMENT_BREAKDOWN}?${queryString}`, { headers })
        .then(transformResponse);
}

//========================== New Overview Functions (Production-Ready) ==========================

/**
 * Helper function to clean params and build query string
 * @param {Object} params - Query parameters
 * @returns {string} - Query string
 */
function buildQueryString(params) {
    const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v != null && v !== '' && v !== undefined)
    );
    return new URLSearchParams(cleanParams).toString();
}

/**
 * Get outstanding liabilities (vouchers + deposits)
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} [params.hairdresserId] - Filter by staff member
 * @returns {Promise} - API response
 */
function getOutstandingLiabilities(params) {
    const headers = getHeaders();
    const queryString = buildQueryString(params);
    return axios.get(`${API.FINANCE_OVERVIEW_LIABILITIES}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Get lost revenue (no-shows + late cancellations)
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} [params.hairdresserId] - Filter by staff member
 * @returns {Promise} - API response
 */
function getLostRevenue(params) {
    const headers = getHeaders();
    const queryString = buildQueryString(params);
    return axios.get(`${API.FINANCE_OVERVIEW_LOST_REVENUE}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Get appointments data with trend
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} [params.granularity] - Data granularity (daily, weekly)
 * @param {string} [params.compareWith] - Comparison period (previous, year, none)
 * @param {string} [params.hairdresserId] - Filter by staff member
 * @returns {Promise} - API response
 */
function getAppointmentsData(params) {
    const headers = getHeaders();
    const queryString = buildQueryString(params);
    return axios.get(`${API.FINANCE_OVERVIEW_APPOINTMENTS}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Get rebooking rate with trend
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} [params.granularity] - Data granularity (daily, weekly)
 * @param {string} [params.compareWith] - Comparison period (previous, year, none)
 * @param {string} [params.hairdresserId] - Filter by staff member
 * @returns {Promise} - API response
 */
function getRebookingRate(params) {
    const headers = getHeaders();
    const queryString = buildQueryString(params);
    return axios.get(`${API.FINANCE_OVERVIEW_REBOOKING_RATE}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Get vouchers data (sold/redeemed)
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @returns {Promise} - API response
 */
function getVouchersData(params) {
    const headers = getHeaders();
    const queryString = buildQueryString(params);
    return axios.get(`${API.FINANCE_OVERVIEW_VOUCHERS}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Get new clients list with source
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {number} [params.limit=10] - Number of clients to return
 * @param {string} [params.hairdresserId] - Filter by staff member
 * @returns {Promise} - API response
 */
function getNewClientsList(params) {
    const headers = getHeaders();
    const queryString = buildQueryString(params);
    return axios.get(`${API.FINANCE_OVERVIEW_NEW_CLIENTS}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Get top clients by spend
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {number} [params.limit=10] - Number of clients to return
 * @param {string} [params.hairdresserId] - Filter by staff member
 * @returns {Promise} - API response
 */
function getTopClients(params) {
    const headers = getHeaders();
    const queryString = buildQueryString(params);
    return axios.get(`${API.FINANCE_OVERVIEW_TOP_CLIENTS}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Get performance metrics (utilization, retention, retail attach rate)
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} [params.compareWith] - Comparison period (previous, year, none)
 * @param {string} [params.hairdresserId] - Filter by staff member
 * @returns {Promise} - API response
 */
function getPerformanceMetrics(params) {
    const headers = getHeaders();
    const queryString = buildQueryString(params);
    return axios.get(`${API.FINANCE_OVERVIEW_PERFORMANCE}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Get all overview data in a single optimized call
 * This is the recommended approach for the overview dashboard to minimize API calls
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} [params.granularity] - Data granularity (daily, weekly)
 * @param {string} [params.compareWith] - Comparison period (previous, year, none)
 * @param {string} [params.hairdresserId] - Filter by staff member
 * @returns {Promise} - API response with all overview data
 */
function getOverviewAll(params) {
    const headers = getHeaders();
    const queryString = buildQueryString(params);
    return axios.get(`${API.FINANCE_OVERVIEW_ALL}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Revenue broken down by location, for the header's Locations filter.
 * One row per selected site, from that site's own aggregation.
 */
function getRevenueBySite(params) {
    const headers = getHeaders();
    const queryString = buildQueryString(params);
    return axios.get(`${API.FINANCE_OVERVIEW_BY_SITE}?${queryString}`, { headers })
        .then(transformResponse);
}

//========================== Transaction Functions ==========================

/**
 * Get transactions list with pagination and filters
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} [params.startDate] - Start date (YYYY-MM-DD)
 * @param {string} [params.endDate] - End date (YYYY-MM-DD)
 * @param {string} [params.search] - Search query (client name, staff name)
 * @param {string} [params.type] - Transaction type filter (all, service, product, commission)
 * @param {string} [params.paymentMethod] - Payment method filter
 * @param {string} [params.hairdresserId] - Filter by staff member
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.limit=50] - Items per page
 * @returns {Promise} - API response
 */
function getTransactions(params) {
    const headers = getHeaders();

    // Clean up params - remove undefined/null values
    const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v != null && v !== '')
    );

    const queryString = new URLSearchParams(cleanParams).toString();

    return axios.get(`${API.FINANCE_TRANSACTIONS}?${queryString}`, { headers })
        .then(transformResponse);
}

/**
 * Get transaction details by ID
 * @param {string} transactionId - Transaction ID (booking ID)
 * @param {Object} [params] - Additional parameters
 * @returns {Promise} - API response
 */
function getTransactionById(transactionId, params = {}) {
    const headers = getHeaders();
    const queryString = params ? new URLSearchParams(cleanParams(params)).toString() : '';
    const url = queryString
        ? `${API.FINANCE_TRANSACTION_DETAIL}/${transactionId}?${queryString}`
        : `${API.FINANCE_TRANSACTION_DETAIL}/${transactionId}`;

    return axios.get(url, { headers })
        .then(transformResponse);
}

/**
 * Get export count and metadata for preview.
 * @param {Object} params - Export parameters (same as exportTransactionsCSV)
 * @returns {Promise} - Count, weight, and system load info
 */
function getExportCount(params) {
    const headers = getHeaders();
    const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v != null && v !== '')
    );
    const queryString = new URLSearchParams(cleanParams).toString();

    return axios.get(`${API.FINANCE_TRANSACTIONS_EXPORT_COUNT}?${queryString}`, { headers })
        .then(response => {
            // Export count endpoint returns data directly on responseData (not nested under responseData.data)
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        data: response.data.responseData
                    }
                };
            }
            return response;
        });
}

/**
 * Export transactions as CSV (streamed download).
 * @param {Object} params - Export parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} [params.search] - Search query
 * @param {string} [params.type] - Transaction type filter
 * @param {string} [params.hairdresserId] - Staff filter
 * @returns {Promise} - Blob response for download
 */
function exportTransactionsCSV(params) {
    const headers = getHeaders();
    const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v != null && v !== '')
    );
    const queryString = new URLSearchParams(cleanParams).toString();

    return axios.get(`${API.FINANCE_TRANSACTIONS_EXPORT}?${queryString}`, {
        headers,
        responseType: 'blob',
        timeout: 300000 // 5 min timeout for large exports
    });
}

//========================== Report Functions ==========================

/**
 * Get available report types
 * @returns {Promise} - API response
 */
function getReportTypes() {
    const headers = getHeaders();

    return axios.get(API.FINANCE_REPORTS_TYPES, { headers })
        .then(transformResponse);
}

/**
 * Generate a report
 * @param {Object} data - Report generation data
 * @param {string} data.salonId - Salon ID
 * @param {string} data.reportType - Report type ID
 * @param {string} data.startDate - Start date (YYYY-MM-DD)
 * @param {string} data.endDate - End date (YYYY-MM-DD)
 * @param {string} [data.hairdresserId] - Filter by staff member
 * @param {string} [data.format] - Output format (json, pdf, csv)
 * @returns {Promise} - API response
 */
function generateReport(data) {
    const headers = getHeaders();

    return axios.post(API.FINANCE_REPORTS_GENERATE, data, { headers })
        .then(transformResponse);
}

/**
 * Get a previously generated report
 * @param {string} reportId - Report ID
 * @returns {Promise} - API response
 */
function getReport(reportId) {
    const headers = getHeaders();

    return axios.get(`${API.FINANCE_REPORTS_GET}/${reportId}`, { headers })
        .then(transformResponse);
}

/**
 * Download report in specified format
 * @param {string} reportId - Report ID
 * @param {string} [format='pdf'] - Download format (pdf, csv)
 * @returns {Promise} - API response
 */
function downloadReport(reportId, format = 'pdf') {
    const headers = getHeaders();

    return axios.get(`${API.FINANCE_REPORTS_DOWNLOAD}/${reportId}/download?format=${format}`, {
        headers,
        responseType: format !== 'json' ? 'blob' : 'json'
    }).then(response => {
        if (format !== 'json' && response.data instanceof Blob) {
            // Create download link for blob
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `report-${reportId}.${format}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            return { data: { status: 1, message: 'Download started' } };
        }
        return transformResponse(response);
    });
}

export default financeService;
